import { useEffect, useMemo, useState, useTransition } from 'react';
import { debounce } from 'lodash';
import { PartialDeep } from 'type-fest';
import { ArrowDown, ArrowDownUp, ArrowRightIcon, ArrowUp, PlusIcon, SearchXIcon } from 'lucide-react';
import { useReadQuery, type QueryRef } from '@apollo/client/react';
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
  type RowSelectionState,
  type SortingState,
} from '@tanstack/react-table';
import { useNavigate } from 'react-router';

import { LegacyRepoPlatformEnum, type ILegacyRepo } from 'src/graphql/schema';
import { PROVIDER_KEY_TO_NAME } from 'src/shared/constants';
import { cn } from 'src/lib/utils';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { InputSearch } from '@/components/ui/input-search';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import Paginator from '@/components/ui/paginator';
import { logoMap, renderLogo } from 'src/utils/renderLogo';
import { Card } from '@/components/ui/card';
import { EmptyBlock } from '@/components/ui/empty-block';

import { useMigrations } from '../model';

import type { IIntegrationsQuery } from 'src/graphql/actions/integration/integrations.query';

export type IMigrationRow = {
  id: string;
  escrow?: string;
  platform?: LegacyRepoPlatformEnum;
  depositSource?: string;
  title: string;
  action: {
    id: string;
    isConnected: boolean;
  };
};

const columnHelper = createColumnHelper<IMigrationRow>();

const MigrateAction = ({ id, isConnected, isSelected }: { id: string; isConnected: boolean; isSelected: boolean }) => {
  const {
    ui: { selectSingle },
  } = useMigrations();

  const onMigrate = () => selectSingle(id);

  if (isConnected)
    return (
      <Button disabled={isSelected} size="sm" onClick={onMigrate}>
        Migrate <ArrowRightIcon />
      </Button>
    );

  return null;
};

const columns = [
  columnHelper.display({
    id: 'select',
    header: ({ table }) => (
      <Checkbox
        checked={table.getIsAllPageRowsSelected() || (table.getIsSomePageRowsSelected() && 'indeterminate')}
        onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
        aria-label="Select all"
        disabled={!table.getRowModel().rows.some((row) => row.getCanSelect())}
      />
    ),
    cell: ({ row }) => (
      <Checkbox
        checked={row.getIsSelected()}
        onCheckedChange={(value) => row.toggleSelected(!!value)}
        disabled={!row.original.action.isConnected}
        aria-label="Select row"
      />
    ),
    enableSorting: false,
  }),
  columnHelper.accessor('platform', {
    header: 'Provider',
    cell: (props) => {
      const provider = props.getValue();
      const providerName = provider ? PROVIDER_KEY_TO_NAME[provider as keyof typeof PROVIDER_KEY_TO_NAME] : '';
      const Icon = renderLogo(provider as keyof typeof logoMap);

      return (
        <div className="flex items-center gap-2">
          {Icon && (
            <div className="px-3">
              <Icon className="size-10 shrink-0" />
            </div>
          )}
          <span className="truncate">{providerName}</span>
        </div>
      );
    },
    enableSorting: false,
  }),
  columnHelper.accessor('escrow', {
    header: 'Escrow',
    enableSorting: false,
  }),
  columnHelper.accessor('title', {
    header: 'Asset title',
    sortingFn: 'text',
  }),
  columnHelper.accessor('depositSource', {
    header: 'Deposit source',
    enableSorting: false,
  }),
  columnHelper.accessor('action', {
    header: '',
    cell: (props) => <MigrateAction {...props.getValue()} isSelected={props.row.getIsSelected()} />,
    enableSorting: false,
    meta: {
      cellClassName: 'text-right',
    },
  }),
];

const MigrationsTableContainer = ({ queryRef }: { queryRef: QueryRef<IIntegrationsQuery> }) => {
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const [sorting, setSorting] = useState<SortingState>([]);
  const {
    legacy: { repos: migrationsRepos, variables: legacyVariables, pagesCount: migrationPagesCount },
    enableBulk,
    ui,
    onLegacyVariablesChange,
  } = useMigrations();
  const [isPending, startTransition] = useTransition();
  const { data: activeIntegrations } = useReadQuery(queryRef);
  const navigate = useNavigate();

  // This effect deselects all rows when failed migrating multiple assets
  useEffect(() => {
    if (ui.selection === 'none') setRowSelection({});
  }, [ui.selection]);

  const connectedProviders = activeIntegrations.integrations.nodes.map(({ provider }) => provider);

  const tableData = useMemo(
    () => migrationsRepos.map((item) => getMigration(item, connectedProviders)),
    [migrationsRepos, connectedProviders.join(',')],
  );

  const activeColumns = enableBulk ? columns : columns.filter(({ id }) => id !== 'select');

  const table = useReactTable({
    data: tableData,
    columns: activeColumns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getRowId: (row) => row.id,
    enableRowSelection: (row) => row.original.action.isConnected,
    onRowSelectionChange: setRowSelection,
    onSortingChange: setSorting,
    state: { rowSelection, sorting },
  });

  const onSearchChange = debounce((e: React.ChangeEvent<HTMLInputElement>) => {
    startTransition(() => onLegacyVariablesChange({ page: 1, queryFilter: e.target.value }));
  }, 500);

  const onPageChange = (page: number) => startTransition(() => onLegacyVariablesChange({ page }));

  const onSearchClear = () => startTransition(() => onLegacyVariablesChange({ page: 1, queryFilter: '' }));

  const onAddIntegration = () => navigate('/integrations');

  const selectedIds = table.getSelectedRowModel().rows.map((row) => row.original.id);
  const handleMultipleMigration = () => ui.selectMultiple(selectedIds);

  return (
    <div className="space-y-6 grow flex flex-col">
      <div className="flex items-center justify-between gap-2">
        <InputSearch
          placeholder="Search"
          className="w-[373px] max-w-full [&>input]:bg-white"
          isLoading={isPending}
          onChange={onSearchChange}
          onClear={onSearchClear}
        />
        <div className="flex items-center gap-2">
          <Button variant="secondary" size="sm" onClick={onAddIntegration}>
            <PlusIcon />
            Add integration
          </Button>
          {Boolean(selectedIds.length) && (
            <Button size="sm" onClick={handleMultipleMigration}>
              Migrate selected <ArrowRightIcon />
            </Button>
          )}
        </div>
      </div>
      <div className="grow relative">
        {migrationsRepos.length ? (
          <Card className="overflow-hidden">
            <Table isLoading={isPending}>
              <TableHeader>
                {table.getHeaderGroups().map((headerGroup) => (
                  <TableRow key={headerGroup.id}>
                    {headerGroup.headers.map((header) => (
                      <TableHead
                        key={header.id}
                        className={cn(
                          'bg-gray-50 text-slate-700 font-bold text-xs first:rounded-tl-md last:rounded-tr-md uppercase whitespace-nowrap',
                          header.column.columnDef.meta?.headerClassName,
                        )}
                      >
                        {header.column.getCanSort() ? (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={header.column.getToggleSortingHandler()}
                            className="-ml-3 text-slate-700! text-xs! font-bold"
                          >
                            {flexRender(header.column.columnDef.header, header.getContext())}
                            {{
                              asc: <ArrowUp />,
                              desc: <ArrowDown />,
                            }[header.column.getIsSorted() as string] ?? <ArrowDownUp />}
                          </Button>
                        ) : (
                          flexRender(header.column.columnDef.header, header.getContext())
                        )}
                      </TableHead>
                    ))}
                  </TableRow>
                ))}
              </TableHeader>
              <TableBody>
                {table.getRowModel().rows.map((row) => (
                  <TableRow key={row.id} className="group/row">
                    {row.getVisibleCells().map((cell) => (
                      <TableCell
                        key={cell.id}
                        className={cn(
                          cell.column.columnDef.meta?.cellClassName,
                          'group-last/row:first:rounded-bl-md group-last/row:last:rounded-br-md',
                        )}
                      >
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        ) : (
          <EmptyBlock
            title="No results found"
            description="There were no results for your search. Try another filter."
            icon={SearchXIcon}
            classes={{
              card: 'absolute top-0 left-0 bottom-0 w-full [&>div]:h-full',
              content: 'h-full flex flex-col justify-center',
              title: 'text-base',
            }}
          />
        )}
        <Paginator
          currentPage={legacyVariables?.page || 1}
          totalPages={migrationPagesCount}
          onPageChange={onPageChange}
          showPreviousNext
          hideOnSinglePage
          className="mt-6 p-2"
        />
      </div>
    </div>
  );
};

function getMigration(item: PartialDeep<ILegacyRepo>, connectedProviders: string[]): IMigrationRow {
  return {
    id: item.id || '',
    escrow: [item.softwareEscrow?.depositor?.companyName, item.softwareEscrow?.beneficiary?.companyName]
      .filter(Boolean)
      .join(' <> '),
    platform: item.platform,
    depositSource: item.url,
    title: item.title || '',
    action: {
      id: item.id || '',
      isConnected: connectedProviders.includes(item.platform || ''),
    },
  };
}

export default MigrationsTableContainer;
